/*
  Shopify path: assets/beulah-quickadd.js
  Beulah Style — working "Quick add +" on product cards, collection tiles AND
  the homepage "Style Highlights" strip.

  The "Quick add +" label on every .bs-card used to be decorative only. This
  script makes it real, using ONE delegated click listener on the document so
  it works for server-rendered cards (snippets/product-card.liquid) AND for
  cards built by JS after page load (the PDP "Recently viewed" row).

  TILES THAT DON'T USE THE CARD SNIPPET: the collection grid
  (sections/main-collection-product-grid.liquid, .plp-tile) and the homepage
  Style Highlights strip (sections/main-index.liquid, .style-item) each render
  their own markup, so neither ever had a Quick add button. Rather than
  rewriting those large sections, enhanceTiles() below injects a .bs-quickadd
  button into every product image on page load. Everything else (panel, sizes,
  add to cart) is the exact same code path as the .bs-card version — the
  selectors just accept any of the three markups. Skipped on purpose: the
  editorial "Beulah Edit" feature tile (.is-feature, not a product) and any
  Style Highlights slot with no product picked (those have no link/.style-item-link,
  so there's nothing to add).

  Flow: click "Quick add +" -> fetch /products/<handle>.js (Shopify's public
  AJAX product endpoint, cached per handle) -> show size buttons inside the
  image box -> click a size -> POST /cart/add.js -> refresh the header cart
  count, close the panel, and fire a "bs:item-added" event. The slide-out cart
  drawer (assets/beulah-cart-drawer.js) listens for that event and opens, so
  the confirmation shoppers see is the drawer itself showing the new item.

  FINDING THE SIZE OPTION: most products name it "Size", but some products
  (imported wholesale data) name the S/M/L option "Prepack", so matching the
  name alone listed those as unreadable "Coffee / S" combined choices. The
  size option is now found by name first (anything containing "size"), and if
  none matches, by its VALUES — the first option whose every value looks like
  a size (XS/S/M/L/XL..., S/M-style pairs, "One Size", plain numbers).

  Colour handling: products here have Size + Colour options (some with 14
  colours). The card photo is the product's first photo, so Quick add uses
  the colour of the first AVAILABLE variant and names that colour in the
  panel title ("Select size · Black") so shoppers always see what they're
  adding. Other colours are chosen on the product page. Sizes that don't exist
  or are sold out in that colour are shown struck-through and disabled.
  Products where no size option can be found list their variants as the
  choices instead.

  Clicks inside the panel call preventDefault because the whole card is an <a>
  (otherwise any tap would navigate to the product page). Listener runs in the
  capture phase so nothing else on the page can swallow these clicks.
*/
(function () {
  if (window.__bsQuickAddLoaded) return;
  window.__bsQuickAddLoaded = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var IMG_SEL = '.bs-card-img, .plp-tile-img, .style-item-img';
  var CARD_SEL = '.bs-card, .plp-tile, .style-item-link';
  var SIZE_VALUE_RE = new RegExp('^(x{0,3}s|x{0,3}l|m|os|one size|osfa|[0-9]{1,2}([.]5)?|(x{0,2}s|s|m|l)/(s|m|l|x{0,2}l))$', 'i');
  var productCache = {};
  var openImg = null;

  function enhanceTiles() {
    var targets = document.querySelectorAll('.plp-tile:not(.is-feature) .plp-tile-img, .style-item-link .style-item-img');
    targets.forEach(function (img) {
      if (img.querySelector('.bs-quickadd')) return;
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bs-quickadd';
      b.textContent = 'Quick add +';
      img.appendChild(b);
    });
  }
  enhanceTiles();

  function handleFromHref(href) {
    href = href || '';
    var i = href.indexOf('/products/');
    if (i < 0) return null;
    var rest = href.slice(i + 10).split('?')[0].split('#')[0].split('/')[0];
    return rest ? decodeURIComponent(rest) : null;
  }

  function fetchProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch(ROOT + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('load'); return r.json(); })
        .catch(function (e) { delete productCache[handle]; throw e; });
    }
    return productCache[handle];
  }

  function variantOptions(v) {
    return v.options || [v.option1, v.option2, v.option3].filter(function (x) { return x != null; });
  }

  function optionNames(p) {
    return (p.options || []).map(function (o) { return typeof o === 'string' ? o : o.name; });
  }

  function findSizeIndex(names, variants) {
    var idx = -1;
    names.forEach(function (n, i) {
      if (idx === -1 && /size/i.test(String(n))) idx = i;
    });
    if (idx !== -1) return idx;

    names.forEach(function (n, i) {
      if (idx !== -1) return;
      var vals = [];
      variants.forEach(function (v) {
        var x = variantOptions(v)[i];
        if (x != null && vals.indexOf(x) === -1) vals.push(x);
      });
      if (vals.length && vals.every(function (x) { return SIZE_VALUE_RE.test(String(x).trim()); })) idx = i;
    });
    return idx;
  }

  function buildChoices(p) {
    var names = optionNames(p);
    var variants = p.variants || [];
    var sizeIdx = findSizeIndex(names, variants);

    if (sizeIdx === -1) {
      return {
        title: variants.length > 1 ? 'Select option' : '',
        choices: variants.map(function (v) {
          return { label: v.title === 'Default Title' ? 'Add to bag' : v.title, id: v.id, available: v.available };
        })
      };
    }

    var base = variants.filter(function (v) { return v.available; })[0] || variants[0];
    var baseOpts = base ? variantOptions(base) : [];
    var others = [];
    baseOpts.forEach(function (o, i) { if (i !== sizeIdx) others.push(o); });

    var sizes = [];
    variants.forEach(function (v) {
      var s = variantOptions(v)[sizeIdx];
      if (sizes.indexOf(s) === -1) sizes.push(s);
    });

    var choices = sizes.map(function (s) {
      var match = variants.filter(function (v) {
        var o = variantOptions(v);
        if (o[sizeIdx] !== s) return false;
        for (var i = 0; i < o.length; i++) { if (i !== sizeIdx && o[i] !== baseOpts[i]) return false; }
        return true;
      })[0];
      return { label: s, id: match ? match.id : null, available: !!(match && match.available) };
    });

    return { title: 'Select size' + (others.length ? ' \u00b7 ' + others.join(' / ') : ''), choices: choices };
  }

  function closeAll(except) {
    document.querySelectorAll('.qa-open').forEach(function (el) {
      if (el !== except) el.classList.remove('qa-open');
    });
    openImg = except || null;
  }

  function setMsg(panel, text, cls) {
    var m = panel.querySelector('.bs-qa-msg');
    if (!m) return;
    m.textContent = text || '';
    m.className = 'bs-qa-msg' + (cls ? ' ' + cls : '');
  }

  function refreshCartCount() {
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var n = cart.item_count;
        document.querySelectorAll('a[href$="/cart"]').forEach(function (a) {
          if (a.classList.contains('bs-label-link') && /^\s*Cart/i.test(a.textContent)) {
            a.textContent = 'Cart (' + n + ')';
          } else if (a.getAttribute('aria-label') === 'Cart') {
            var badge = a.querySelector('.bs-cart-count');
            if (n > 0) {
              if (!badge) { badge = document.createElement('span'); badge.className = 'bs-cart-count'; a.appendChild(badge); }
              badge.textContent = n;
            } else if (badge) {
              badge.remove();
            }
          }
        });
        document.dispatchEvent(new CustomEvent('bs:cart-updated', { detail: cart }));
      })
      .catch(function () {});
  }

  function openPanel(trigger) {
    var img = trigger.closest(IMG_SEL);
    var card = trigger.closest(CARD_SEL);
    if (!img || !card) return;

    if (img.classList.contains('qa-open')) { closeAll(); return; }
    closeAll(img);

    var handle = handleFromHref(card.getAttribute('href'));
    if (!handle) { window.location.href = card.href; return; }

    var panel = img.querySelector('.bs-qa-panel');
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'bs-qa-panel';
      img.appendChild(panel);
    }
    panel.innerHTML = '<div class="bs-qa-head"><span class="bs-qa-title">Loading...</span><button type="button" class="bs-qa-close" aria-label="Close">&times;</button></div><div class="bs-qa-sizes"></div><div class="bs-qa-msg" aria-live="polite"></div>';
    img.classList.add('qa-open');

    fetchProduct(handle).then(function (p) {
      var built = buildChoices(p);
      var sizesEl = panel.querySelector('.bs-qa-sizes');
      panel.querySelector('.bs-qa-title').textContent = built.title;
      sizesEl.innerHTML = '';
      var anyAvailable = false;
      built.choices.forEach(function (c) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'bs-qa-size';
        b.textContent = c.label;
        if (c.available && c.id) {
          b.setAttribute('data-variant-id', c.id);
          anyAvailable = true;
        } else {
          b.disabled = true;
          b.setAttribute('data-soldout', '1');
          b.title = 'Sold out';
        }
        sizesEl.appendChild(b);
      });
      if (!anyAvailable) setMsg(panel, 'Sold out in this colour.', '');
      panel.setAttribute('data-handle', handle);
    }).catch(function () {
      panel.querySelector('.bs-qa-title').textContent = '';
      setMsg(panel, 'Could not load sizes. Please open the product page.', 'is-error');
    });
  }

  function addVariant(panel, btn) {
    var id = parseInt(btn.getAttribute('data-variant-id'), 10);
    var handle = panel.getAttribute('data-handle');
    var all = panel.querySelectorAll('.bs-qa-size');
    all.forEach(function (b) { b.disabled = true; });
    setMsg(panel, 'Adding...', '');

    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error((res.d && (res.d.description || res.d.message)) || 'Could not add to bag.');
        setMsg(panel, 'Added to bag', 'is-ok');
        if (handle) delete productCache[handle];
        refreshCartCount().then(function () {
          closeAll();
          document.dispatchEvent(new CustomEvent('bs:item-added'));
        });
      })
      .catch(function (err) {
        all.forEach(function (b) { if (!b.getAttribute('data-soldout')) b.disabled = false; });
        setMsg(panel, err.message || 'Could not add to bag.', 'is-error');
      });
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var trigger = t.closest('.bs-quickadd');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      openPanel(trigger);
      return;
    }

    var panel = t.closest('.bs-qa-panel');
    if (panel) {
      e.preventDefault();
      e.stopPropagation();
      if (t.closest('.bs-qa-close')) { closeAll(); return; }
      var size = t.closest('.bs-qa-size');
      if (size && !size.disabled && size.getAttribute('data-variant-id')) addVariant(panel, size);
      return;
    }

    if (openImg) closeAll();
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openImg) closeAll();
  });
})();
